import React, { useState, useEffect } from 'react';
import { X, Clock, Calendar as CalendarIcon, AlertCircle, Trash2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { API_BASE_URL } from '../../config/api';
import type { AvailabilityType, WorkScheduleEvent } from './WorkScheduleCalendar';
import { RecurringEventActionModal } from './RecurringEventActionModal';
import { useEscapeKey } from '../../hooks/useEscapeKey';

interface WorkScheduleEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  event: WorkScheduleEvent | null;
}

const WorkScheduleEditModal: React.FC<WorkScheduleEditModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  event,
}) => {
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRecurringDeleteModal, setShowRecurringDeleteModal] = useState(false);
  const [showRecurringSaveModal, setShowRecurringSaveModal] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState<any>(null);

  useEscapeKey(onClose, isOpen && !showRecurringDeleteModal && !showRecurringSaveModal);

  // Form state
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [availabilityType, setAvailabilityType] = useState<AvailabilityType>('AVAILABLE');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');

  // Initialize form with event data
  useEffect(() => {
    if (isOpen && event) {
      const start = parseISO(event.start_at);
      const end = parseISO(event.end_at);
      
      setStartDate(format(start, 'yyyy-MM-dd'));
      setStartTime(format(start, 'HH:mm'));
      setEndDate(format(end, 'yyyy-MM-dd'));
      setEndTime(format(end, 'HH:mm'));
      setAvailabilityType(event.availability_type);
      setTitle(event.title || '');
      setNotes(event.notes || '');
      setError(null);
      setShowDeleteConfirm(false);
      setShowRecurringSaveModal(false);
      setPendingSaveData(null);
    }
  }, [isOpen, event]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate times
    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(`${endDate}T${endTime}`);

    if (endDateTime <= startDateTime) {
      setError('End time must be after start time');
      return;
    }

    // Check if this is a recurring event
    if (event?.is_recurring || event?.recurring_template_id) {
      console.log('✅ [WorkScheduleEditModal] Recurring event - showing save options modal');
      console.log('📋 [WorkScheduleEditModal] Current modal state before:', { showRecurringSaveModal, loading, deleting });
      // Store the pending save data
      setPendingSaveData({
        start_at: startDateTime.toISOString(),
        end_at: endDateTime.toISOString(),
        availability_type: availabilityType,
        title: title || undefined,
        notes: notes || null,
      });
      // Show the recurring save modal
      setShowRecurringSaveModal(true);
      console.log('📋 [WorkScheduleEditModal] Set showRecurringSaveModal to true');
      return;
    }

    // Non-recurring event: save directly
    await performSave();
  };

  const performSave = async (scope?: 'single' | 'following' | 'all') => {
    console.log('🚀 [WorkScheduleEditModal] performSave called with scope:', scope);
    setError(null);
    setLoading(true);

    try {
      const startDateTime = new Date(`${startDate}T${startTime}`);
      const endDateTime = new Date(`${endDate}T${endTime}`);
      const jwt_token = localStorage.getItem('token');

      console.log('📝 [WorkScheduleEditModal] Form data:', {
        startDate,
        startTime,
        endDate,
        endTime,
        availabilityType,
        startDateTime: startDateTime.toISOString(),
        endDateTime: endDateTime.toISOString()
      });

      // Check if this is a recurring event
      if (event?.is_recurring || event?.recurring_template_id) {
        console.log('💾 [WorkScheduleEditModal] Saving recurring event with scope:', scope);
        const templateId = event?.recurring_template_id || event?.id;
        console.log('📌 [WorkScheduleEditModal] Template ID:', templateId);

        if (scope === 'single') {
          // RFC 5545: Modify single instance
          const instanceDate = format(parseISO(event!.start_at), 'yyyy-MM-dd');
          const url = `${API_BASE_URL}/api/v1/scheduling/recurring-availability/${templateId}/modify-single?instance_date=${instanceDate}`;
          const body = {
            new_start_at: startDateTime.toISOString(),
            new_end_at: endDateTime.toISOString(),
            new_availability_type: availabilityType,
          };
          
          console.log('📤 [WorkScheduleEditModal] POST modify-single:', { url, body });
          
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${jwt_token}`,
            },
            body: JSON.stringify(body),
          });

          console.log('📥 [WorkScheduleEditModal] Response status:', response.status);

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
            console.error('❌ [WorkScheduleEditModal] Error response:', errorData);
            throw new Error(errorData.detail || 'Failed to update this occurrence');
          }
          
          console.log('✅ [WorkScheduleEditModal] Single instance updated successfully');
        } else if (scope === 'following') {
          // RFC 5545: Split and create new series
          const instanceDate = format(parseISO(event!.start_at), 'yyyy-MM-dd');
          const newStartTime = format(startDateTime, 'HH:mm:ss');
          const newEndTime = format(endDateTime, 'HH:mm:ss');
          
          const params = new URLSearchParams({
            split_date: instanceDate,
            new_start_time: newStartTime,
            new_end_time: newEndTime,
            new_availability_type: availabilityType,
          });
          
          const url = `${API_BASE_URL}/api/v1/scheduling/recurring-availability/${templateId}/split?${params}`;
          console.log('📤 [WorkScheduleEditModal] POST split:', url);
          
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${jwt_token}`,
            },
          });

          console.log('📥 [WorkScheduleEditModal] Response status:', response.status);

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
            console.error('❌ [WorkScheduleEditModal] Error response:', errorData);
            throw new Error(errorData.detail || 'Failed to update this and following occurrences');
          }
          
          console.log('✅ [WorkScheduleEditModal] Following instances updated successfully');
        } else if (scope === 'all') {
          // Update all events in the series
          const url = `${API_BASE_URL}/api/v1/scheduling/recurring-availability/${templateId}`;
          const body = {
            start_time: format(startDateTime, 'HH:mm:ss'),
            end_time: format(endDateTime, 'HH:mm:ss'),
            availability_type: availabilityType,
            title: title || undefined,
          };
          
          console.log('📤 [WorkScheduleEditModal] PUT update-all:', { url, body });
          
          const response = await fetch(url, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${jwt_token}`,
            },
            body: JSON.stringify(body),
          });

          console.log('📥 [WorkScheduleEditModal] Response status:', response.status);

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
            console.error('❌ [WorkScheduleEditModal] Error response:', errorData);
            throw new Error(errorData.detail || 'Failed to update all occurrences');
          }
          
          console.log('✅ [WorkScheduleEditModal] All instances updated successfully');
        }
      } else {
        // Non-recurring event: use regular endpoint
        console.log('💾 [WorkScheduleEditModal] Saving non-recurring event');
        const saveData = {
          start_at: startDateTime.toISOString(),
          end_at: endDateTime.toISOString(),
          availability_type: availabilityType,
          title: title || undefined,
          notes: notes || null,
        };

      const response = await fetch(
        `${API_BASE_URL}/api/v1/scheduling-unix/vet-availability/${event?.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
              'Authorization': `Bearer ${jwt_token}`,
          },
            body: JSON.stringify(saveData),
        }
      );

      if (!response.ok) {
          const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
          console.error('❌ [WorkScheduleEditModal] Error response:', errorData);
        throw new Error(errorData.detail || 'Failed to update availability');
        }
      }

      console.log('✅ [WorkScheduleEditModal] Save successful, closing modal and refreshing');
      setShowRecurringSaveModal(false);
      setPendingSaveData(null);
      onSuccess();
    } catch (err: any) {
      console.error('❌ [WorkScheduleEditModal] Error updating availability:', err);
      // Better error message extraction
      const errorMessage = err.message || (typeof err === 'string' ? err : 'Failed to update availability');
      setError(errorMessage);
      setShowRecurringSaveModal(false); // Close the modal so user can see the error in the edit form
      throw err; // Re-throw so RecurringEventActionModal knows it failed
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    // RFC 5545: Check if this is a recurring event
    if (event?.is_recurring || event?.recurring_template_id) {
      // Show modal to ask user what they want to delete
      setShowDeleteConfirm(false);  // Hide simple confirm
      setShowRecurringDeleteModal(true);  // Show recurring modal
      return;
    }

    // Non-recurring event: proceed with simple delete
    await performDelete();
  };

  const performDelete = async (scope?: 'single' | 'following' | 'all') => {
    setError(null);
    setDeleting(true);

    try {
      const jwt_token = localStorage.getItem('token');
      const templateId = event?.recurring_template_id || event?.id;

      if (scope === 'single') {
        // RFC 5545: Add EXDATE to mark this occurrence as deleted
        const instanceDate = format(parseISO(event!.start_at), 'yyyy-MM-dd');
        const response = await fetch(
          `${API_BASE_URL}/api/v1/scheduling/recurring-availability/${templateId}/delete-single?instance_date=${instanceDate}`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${jwt_token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to delete this occurrence');
        }
      } else if (scope === 'following') {
        // RFC 5545: Set UNTIL date to day before this occurrence
        const instanceDate = parseISO(event!.start_at);
        const endDate = new Date(instanceDate);
        endDate.setDate(endDate.getDate() - 1);
        const effectiveUntil = format(endDate, 'yyyy-MM-dd');

        const response = await fetch(
          `${API_BASE_URL}/api/v1/scheduling/recurring-availability/${templateId}`,
          {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${jwt_token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              effective_until: effectiveUntil
            })
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to delete following occurrences');
        }
      } else if (scope === 'all') {
        // Delete the entire recurring template
        const response = await fetch(
          `${API_BASE_URL}/api/v1/scheduling/recurring-availability/${templateId}`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${jwt_token}`
            }
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to delete all occurrences');
        }
      } else {
        // Non-recurring event: simple delete
        const response = await fetch(
          `${API_BASE_URL}/api/v1/scheduling-unix/vet-availability/${event?.id}`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${jwt_token}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to delete availability');
        }
      }

      setShowRecurringDeleteModal(false);
      setDeleting(false);
      onSuccess();
    } catch (err: any) {
      console.error('Error deleting availability:', err);
      setError(err.message || 'Failed to delete availability');
      setDeleting(false);
    }
  };

  if (!isOpen || !event) return null;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black bg-opacity-30 transition-opacity"
      onClick={onClose}
    >
      <div
        className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl transform transition-transform duration-300 ease-out overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ transform: isOpen ? 'translateX(0)' : 'translateX(100%)' }}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold text-gray-900">Edit Availability</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            )}

            {/* Availability Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Availability Type
              </label>
              <select
                value={availabilityType}
                onChange={(e) => setAvailabilityType(e.target.value as AvailabilityType)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="AVAILABLE">Available</option>
                <option value="SURGERY_BLOCK">Surgery Block</option>
                <option value="UNAVAILABLE">Unavailable</option>
                <option value="EMERGENCY_ONLY">Emergency Only</option>
              </select>
            </div>

            {/* Title (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title <span className="text-gray-400 text-xs">(optional)</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Lunch Break, Surgery Hours"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                maxLength={255}
              />
            </div>

            {/* Start Date and Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <div className="relative">
                  <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Time
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
            </div>

            {/* End Date and Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <div className="relative">
                  <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Time
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={500}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="Add any notes about this availability block..."
              />
              <p className="text-xs text-gray-500 mt-1">{notes.length}/500 characters</p>
            </div>

            {/* Delete Confirmation */}
            {showDeleteConfirm && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                <p className="text-sm text-amber-800 mb-3">
                  Are you sure you want to delete this availability block? This action cannot be undone.
                </p>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-800 transition-colors"
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {deleting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between sticky bottom-0 bg-white">
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={loading || deleting || showDeleteConfirm}
              className="px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 transition-colors"
                disabled={loading || deleting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || deleting}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
      
      {/* RFC 5545 Recurring Event Delete Modal */}
      <RecurringEventActionModal
        isOpen={showRecurringDeleteModal}
        actionType="delete"
        onClose={() => setShowRecurringDeleteModal(false)}
        onConfirm={async (scope) => {
          await performDelete(scope);
          // performDelete will close the modal on success
        }}
      />

      {/* RFC 5545 Recurring Event Save Modal */}
      <RecurringEventActionModal
        isOpen={showRecurringSaveModal}
        actionType="edit"
        onClose={() => {
          console.log('🚪 [WorkScheduleEditModal] Save modal onClose called');
          setShowRecurringSaveModal(false);
          setPendingSaveData(null);
        }}
        onConfirm={async (scope) => {
          console.log('✅ [WorkScheduleEditModal] Save modal onConfirm called with scope:', scope);
          await performSave(scope);
          // performSave will close the modal on success
        }}
      />
    </div>
  );
};

export default WorkScheduleEditModal;

